import { useState } from 'react';
import { formatEur, parseEurCents } from '@/lib/cashFlow';
import type {
  LoanInvoiceAllocationInput,
  NormalizedLoanInvoiceCandidate,
  NormalizedLoanInvoiceRelation,
} from '@/lib/loanInvoiceLinks';

interface Props {
  currentEntryId: string;
  relations: NormalizedLoanInvoiceRelation[];
  candidates: NormalizedLoanInvoiceCandidate[];
  isLoading?: boolean;
  isLinking?: boolean;
  isUnlinking?: boolean;
  error?: string | null;
  onLink: (candidate: NormalizedLoanInvoiceCandidate, allocations: LoanInvoiceAllocationInput) => void;
  onUnlink: (relation: NormalizedLoanInvoiceRelation) => void;
  onOpenEntry?: (entryId: string) => void;
}

const dateLabel = (value: string) =>
  new Intl.DateTimeFormat('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
    new Date(`${value}T00:00:00`),
  );

function centsInput(cents: number): string {
  const euros = Math.floor(cents / 100);
  const remainder = String(cents % 100).padStart(2, '0');
  return `${euros},${remainder}`;
}

function RelationIdentity({
  currentEntryId,
  relation,
  onOpenEntry,
}: {
  currentEntryId: string;
  relation: NormalizedLoanInvoiceRelation;
  onOpenEntry?: (id: string) => void;
}) {
  const viewingInvoice = currentEntryId === relation.invoiceEntryId;
  const targetEntryId = viewingInvoice ? relation.loanEntryId : relation.invoiceEntryId;
  const targetLabel = viewingInvoice
    ? `Osamakse ${relation.seq} · ${relation.lender}`
    : `Arve ${relation.invoiceDocNumber ?? relation.invoiceEntryId} · ${relation.invoiceParty}`;

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-[11px] text-foreground">
        <span className="font-medium">Osamakse {relation.seq}</span>
        <span className="text-muted-foreground/60">·</span>
        <span className="truncate text-muted-foreground">{relation.lender}</span>
      </div>
      <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[10px] text-muted-foreground">
        <span>{viewingInvoice ? 'Laenumakse' : 'Arve'}</span>
        {onOpenEntry ? (
          <button
            type="button"
            onClick={() => onOpenEntry(targetEntryId)}
            className="truncate font-medium text-[hsl(var(--primary))] hover:underline"
          >
            {targetLabel}
          </button>
        ) : (
          <span className="truncate">{targetLabel}</span>
        )}
      </div>
    </div>
  );
}

function Components({
  principalCents,
  interestCents,
  feeCents,
  invoiceGrossCents,
}: LoanInvoiceAllocationInput & { invoiceGrossCents: number }) {
  return (
    <div className="grid grid-cols-3 gap-1 border-t border-border/60 pt-2 text-[10px]">
      {[
        ['Põhiosa', principalCents],
        ['Intress', interestCents],
        ['Tasu', feeCents],
      ].map(([label, amount]) => (
        <div key={label as string} className="min-w-0">
          <div className="text-muted-foreground">{label}</div>
          <div className="truncate tabular-nums text-foreground/85">{formatEur(amount as number)}</div>
        </div>
      ))}
      <div className="col-span-3 flex justify-between border-t border-border/40 pt-1">
        <span className="text-muted-foreground">Arve kokku</span>
        <span className="font-medium tabular-nums text-foreground">{formatEur(invoiceGrossCents)}</span>
      </div>
    </div>
  );
}

export function LoanInvoiceLinkPanel({
  currentEntryId,
  relations,
  candidates,
  isLoading = false,
  isLinking = false,
  isUnlinking = false,
  error = null,
  onLink,
  onUnlink,
  onOpenEntry,
}: Props) {
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [edits, setEdits] = useState<Record<string, LoanInvoiceAllocationInput>>({});
  const [drafts, setDrafts] = useState<
    Record<string, Partial<Record<keyof LoanInvoiceAllocationInput, string>>>
  >({});

  const allocationFor = (candidate: NormalizedLoanInvoiceCandidate): LoanInvoiceAllocationInput =>
    edits[candidate.id] ?? {
      principalCents: candidate.proposedPrincipalCents,
      interestCents: candidate.proposedInterestCents,
      feeCents: candidate.proposedFeeCents,
    };

  const updateAllocation = (
    candidate: NormalizedLoanInvoiceCandidate,
    key: keyof LoanInvoiceAllocationInput,
    raw: string,
  ) => {
    setDrafts((current) => ({
      ...current,
      [candidate.id]: { ...current[candidate.id], [key]: raw },
    }));
    const parsed = parseEurCents(raw);
    if (parsed === null) return;
    setEdits((current) => ({
      ...current,
      [candidate.id]: { ...allocationFor(candidate), [key]: parsed },
    }));
  };

  return (
    <section data-testid="loan-invoice-link-panel" className="rounded-lg border border-border bg-card/40">
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Laenumakse seos</h3>
        {relations.length > 0 && <span className="text-[10px] text-muted-foreground">{relations.length} seotud</span>}
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="ml-auto rounded px-1.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          Seo laenumaksega <span aria-hidden>{open ? '▾' : '▸'}</span>
        </button>
      </header>

      {relations.length > 0 && (
        <ul className="divide-y divide-border/60">
          {relations.map((relation) => (
            <li key={relation.linkId} className="px-3 py-2">
              <div className="flex items-start gap-2">
                <RelationIdentity
                  currentEntryId={currentEntryId}
                  relation={relation}
                  onOpenEntry={onOpenEntry}
                />
                <div className="ml-auto shrink-0 text-right">
                  <div className="text-[11px] font-medium tabular-nums text-foreground">
                    {formatEur(relation.invoiceGrossCents)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">tähtaeg {dateLabel(relation.dueDate)}</div>
                </div>
              </div>
              <Components
                principalCents={relation.principalCents}
                interestCents={relation.interestCents}
                feeCents={relation.feeCents}
                invoiceGrossCents={relation.invoiceGrossCents}
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[10px] text-muted-foreground">
                  Arvelt tasutud {formatEur(relation.invoiceSettledCents)}
                </span>
                <button
                  type="button"
                  disabled={!relation.canUnlink || isUnlinking}
                  title={
                    relation.canUnlink
                      ? 'Eemalda seos'
                      : relation.unlinkBlockedReason ?? 'Seost ei saa eemaldada'
                  }
                  onClick={() => onUnlink(relation)}
                  className="rounded border border-[hsl(var(--cost))]/40 px-2 py-1 text-[10px] text-[hsl(var(--cost))] hover:bg-[hsl(var(--cost))]/10 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {isUnlinking ? 'Eemaldan…' : 'Eemalda seos'}
                </button>
              </div>
              {!relation.canUnlink && (
                <p className="mt-1 text-right text-[10px] text-[hsl(var(--cost))]">
                  {relation.unlinkBlockedReason}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="border-t border-border/70 px-3 py-2">
          {isLoading ? (
            <div data-testid="loan-candidate-skeleton" className="space-y-2">
              <div className="h-8 animate-pulse rounded bg-muted/50" />
              <div className="h-8 animate-pulse rounded bg-muted/50" />
            </div>
          ) : error ? (
            <p role="alert" className="py-1 text-[11px] text-[hsl(var(--cost))]">{error}</p>
          ) : candidates.length === 0 ? (
            <p className="py-1 text-[11px] text-muted-foreground">Sobivat arvet või laenumakset ei leitud.</p>
          ) : (
            <ul className="space-y-2">
              {candidates.map((candidate) => {
                const allocation = allocationFor(candidate);
                const total =
                  allocation.principalCents + allocation.interestCents + allocation.feeCents;
                const withinCapacity =
                  allocation.principalCents <= candidate.availablePrincipalCents
                  && allocation.interestCents <= candidate.availableInterestCents
                  && allocation.feeCents <= candidate.availableFeeCents;
                const valid = total === candidate.invoiceGrossCents && withinCapacity;
                const needsConfirmation = !candidate.exactMatch;
                return (
                  <li
                    key={candidate.id}
                    data-testid={`loan-candidate-${candidate.id}`}
                    className={`rounded border p-2 ${
                      candidate.exactMatch
                        ? 'border-[hsl(var(--income))]/45 bg-[hsl(var(--income))]/5'
                        : 'border-[hsl(var(--pending))]/45 bg-[hsl(var(--pending))]/5'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className="font-medium">
                            Osamakse {candidate.seq} · arve {candidate.invoiceDocNumber ?? candidate.invoiceEntryId}
                          </span>
                          <span
                            className={`rounded border px-1 text-[9px] uppercase ${
                              candidate.exactMatch
                                ? 'border-[hsl(var(--income))]/40 text-[hsl(var(--income))]'
                                : 'border-[hsl(var(--pending))]/40 text-[hsl(var(--pending))]'
                            }`}
                          >
                            {candidate.exactMatch ? 'Täpselt' : 'Kontrolli'}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[10px] text-muted-foreground">
                          {candidate.lender} · {candidate.invoiceParty} · tähtaeg {dateLabel(candidate.dueDate)}
                          {' · '}{formatEur(candidate.invoiceGrossCents)}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={
                          isLinking
                          || !valid
                          || (needsConfirmation && !confirmed[candidate.id])
                        }
                        onClick={() => onLink(candidate, allocation)}
                        className="shrink-0 rounded bg-[hsl(var(--primary))] px-2 py-1 text-[10px] font-semibold text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {isLinking ? 'Seon…' : 'Seo'}
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-1">
                      {([
                        ['principalCents', 'Põhiosa', candidate.availablePrincipalCents],
                        ['interestCents', 'Intress', candidate.availableInterestCents],
                        ['feeCents', 'Tasu', candidate.availableFeeCents],
                      ] as const).map(([key, label, available]) => (
                        <label key={key} className="text-[10px] text-muted-foreground">
                          {label} <span className="text-muted-foreground/60">/{formatEur(available)}</span>
                          <input
                            aria-label={`${candidate.id} ${label}`}
                            inputMode="decimal"
                            value={drafts[candidate.id]?.[key] ?? centsInput(allocation[key])}
                            onChange={(event) => updateAllocation(candidate, key, event.target.value)}
                            disabled={!needsConfirmation || isLinking}
                            className="mt-0.5 w-full rounded border border-border bg-background/50 px-1.5 py-1 text-right text-[11px] tabular-nums text-foreground outline-none focus:border-[hsl(var(--primary))]"
                          />
                        </label>
                      ))}
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[10px]">
                      <span className={valid ? 'text-muted-foreground' : 'text-[hsl(var(--cost))]'}>
                        Jaotus {formatEur(total)} / arve {formatEur(candidate.invoiceGrossCents)}
                        {!withinCapacity && ' · ületab vaba mahu'}
                      </span>
                      {needsConfirmation && (
                        <label className="flex shrink-0 items-center gap-1 text-[hsl(var(--pending))]">
                          <input
                            type="checkbox"
                            checked={confirmed[candidate.id] ?? false}
                            onChange={(event) =>
                              setConfirmed((current) => ({
                                ...current,
                                [candidate.id]: event.target.checked,
                              }))
                            }
                          />
                          Kinnitan erinevuse
                        </label>
                      )}
                    </div>
                    {candidate.reasons.length > 0 && (
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {candidate.reasons.join(' · ')}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}